///
/// EarthBrowser
///
/// Created by Matt Giger on 4/21/2024
/// Copyright (c) 2024 Geodata Labs
///

import { DataLoader } from "@/earthbrowser/utils/loaders/DataLoader"
import { ComponentType, DrawMode, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { ShaderSourceDefinition, combineDefines } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { ZipArchive } from "@/earthbrowser/utils/ZipArchive"
import { logger } from "@/utils/LogDispatcher"
import { GLTFGeometry } from "./GLTFGeometry"
import { GLTFMaterial, GLTFMaterialDefault } from "./GLTFMaterial"
import { GLTFMeshPrimitive } from "./GLTFMeshPrimitive"
import { GLTFNode } from "./GLTFNode"
import { GLTFRenderer } from "./GLTFRenderer"
import type { GLTFAccessorType, GLTFBufferType, GLTFBufferViewType, GLTFContainerType, GLTFPrimitiveType } from "./GLTFTypes"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class GLTF {
	public container: GLTFContainerType = { asset: { version: "2.0" }, scene: 0 }
	private archive?: ZipArchive
	private buffers = new Map<string, ArrayBufferLike>()
	private textures = new Map<number, Texture>()
	private glbDataBuffer?: ArrayBufferLike
	private context: StovokorContext
	constructor(context: StovokorContext) {
		this.context = context
	}

	public async loadModel(url: string): Promise<GLTFNode[]> {

		const loader = new DataLoader(url)
		const data = await loader.load()
		if(!data) {
			return []
		}

		if(ZipArchive.isZipArchive(data)) {
			this.archive = new ZipArchive(data)
			const definitionFile = this.archive.files.filter(f => f.fileName.endsWith(".gltf"))
			if(definitionFile.length > 0) {
				const data = await definitionFile[0].extract()
				const text = new TextDecoder('utf8').decode(new Uint8Array(data))
				this.container = JSON.parse(text) as GLTFContainerType
			}

		} else {

			const view = new DataView(data)
			const magic = view.getUint32(0, true)
			if(magic == 0x46546C67) {
				const version = view.getUint32(4, true)
				const length = view.getUint32(8, true)

				// try to load as a glb file
				try {
					if(version == 2 && length == data.byteLength) {
						let offset = 12

						while(offset < data.byteLength) {
							if(offset + 8 > data.byteLength) {
								break // Not enough data for chunk header
							}

							const chunkLength = view.getUint32(offset, true)
							const chunkType = view.getUint32(offset + 4, true)

							if(offset + 8 + chunkLength > data.byteLength) {
								logger.error(`[GLTF] Invalid GLB chunk size at offset ${offset}`)
								return Promise.reject(new Error("Invalid GLB chunk size"))
							}

							if(chunkType == 0x4E4F534A) {	// JSON
								const chunk = data.slice(offset + 8, offset + 8 + chunkLength)
								const chunkText = new TextDecoder('utf8').decode(new Uint8Array(chunk))
								this.container = JSON.parse(chunkText) as GLTFContainerType

							} else if(chunkType == 0x004E4942) {	// BIN
								this.glbDataBuffer = data.slice(offset + 8, offset + 8 + chunkLength)

							} else {
								// Unknown chunk types should be ignored per spec, not rejected
								logger.warn(`[GLTF] Unknown GLB chunk type: 0x${chunkType.toString(16)}`)
							}

							offset += 8 + chunkLength
						}
					} else {
						logger.error(`[GLTF] Version mismatch or length mismatch: version=${version}, expected length=${length}, actual=${data.byteLength}`)
					}
				} catch(error) {
					const errorMsg = error instanceof Error ? error.message : String(error)
					logger.error(`[GLTF] Failed to load GLTF file: ${errorMsg}`)
					return Promise.reject(new Error("Invalid GLTF file"))
				}

			} else {
				logger.warn(`[GLTF] Unknown file format, magic: 0x${magic.toString(16)}`)
			}
		}

		// Per glTF 2.0 spec: scenes array is optional
		// If no scenes, load all top-level nodes directly
		let nodeIndices: number[] | undefined

		if(this.container.scenes && this.container.scenes.length > 0) {
			// Load nodes from specified scene
			const sceneIndex = this.container.scene !== undefined ? this.container.scene : 0

			if(sceneIndex >= this.container.scenes.length) {
				logger.warn(`[GLTF] Scene index ${sceneIndex} out of range, using scene 0`)
			}

			const scene = this.container.scenes[Math.min(sceneIndex, this.container.scenes.length - 1)]
			nodeIndices = scene?.nodes
		} else if(this.container.nodes) {
			// No scenes defined, load all top-level nodes
			nodeIndices = this.container.nodes.map((_, index) => index)
		}

		if(!nodeIndices || nodeIndices.length === 0) {
			return []
		}

		const models: GLTFNode[] = []
		for(const nodeIndex of nodeIndices) {
			const cnode = this.container.nodes?.[nodeIndex]
			if(!cnode) {
				continue
			}
			try {
				const node = new GLTFNode(cnode, this)
				await node.loadData()
				models.push(node)
			} catch(error) {
				logger.error(error)
			}
		}

		return models
	}

	public purge(): void {
		this.buffers.clear()
		this.textures.clear()
	}

	public async loadMesh(meshIndex: number): Promise<GLTFMeshPrimitive[]> {
		const meshes: GLTFMeshPrimitive[] = []
		if(!this.container.meshes || meshIndex >= this.container.meshes.length) {
			return meshes
		}

		const mesh = this.container.meshes[meshIndex]
		for(const primitive of mesh.primitives) {
			const m = await this.loadMeshPrimitive(primitive)
			meshes.push(m)
		}
		return meshes
	}

	protected async loadMeshPrimitive(primitive: GLTFPrimitiveType): Promise<GLTFMeshPrimitive> {
		const accessors = this.container.accessors
		if(!accessors) {
			return Promise.reject(new Error("Failed to mesh primitive"))
		}

		const attributes = new Map<string, VertexAttribute>()
		const keys = Object.keys(primitive.attributes)
		for(const key of keys) {
			const accessor = accessors[primitive.attributes[key]]
			if(accessor) {
				const attribute = await this.accessorToAttribute(accessor)
				if(attribute) {
					attributes.set(key, attribute)
				}
			}
		}

		const mode = primitive.mode ?? DrawMode.Triangles
		let indexBuffer: IndexBuffer | undefined
		if(primitive.indices !== undefined && accessors[primitive.indices]) {
			indexBuffer = await this.accessorToIndexBuffer(accessors[primitive.indices])
		}

		// *********************************************************************************
		// This is where it all comes together to create the primitive
		// *********************************************************************************

		let material: GLTFMaterial
		const materials = this.container.materials
		if(materials && primitive.material !== undefined && primitive.material < materials.length) {
			material = new GLTFMaterial()
			await material.load(materials[primitive.material], this)
		} else {
			material = new GLTFMaterialDefault()
		}
		const geometry = new GLTFGeometry(this.context, mode, attributes, indexBuffer)
		const defines = combineDefines([geometry.requiredAttributes(), material.requiredAttributes()])
		const source = new ShaderSourceDefinition("gltf", defines)
		const renderer = new GLTFRenderer(this.context, source, geometry, material)
		const prim = new GLTFMeshPrimitive(geometry, renderer)
		return prim
	}

	public async loadTexture(textureIndex: number): Promise<Texture> {
		const textures = this.container.textures
		if(!textures || textureIndex >= textures.length) {
			return Promise.reject(new Error("Failed to load texture"))
		}

		const cachedTex = this.textures.get(textureIndex)
		if(cachedTex) {
			return cachedTex
		}

		const textureType = textures[textureIndex]
		const texture = new Texture(this.context)
		if(textureType.sampler !== undefined && this.container.samplers && textureType.sampler < this.container.samplers.length) {
			const sampler = this.container.samplers[textureType.sampler]
			if(sampler !== undefined) {
				texture.minFilter = sampler.minFilter ?? TextureFilter.LinearMipmapLinear
				texture.magFilter = sampler.magFilter ?? TextureFilter.Linear
				texture.wrapS = sampler.wrapS ?? TextureWrap.Repeat
				texture.wrapT = sampler.wrapT ?? TextureWrap.Repeat
			}
		} else {
			// No sampler specified - use spec defaults
			texture.minFilter = TextureFilter.LinearMipmapLinear
			texture.magFilter = TextureFilter.Linear
			texture.wrapS = TextureWrap.Repeat
			texture.wrapT = TextureWrap.Repeat
		}
		const buff = await this.loadImageBuffer(textureType.source)
		await texture.setFromArrayBuffer(new Uint8Array(buff))

		this.textures.set(textureIndex, texture)
		return texture
	}

	public async loadImageBuffer(imageIndex: number): Promise<ArrayBufferLike> {
		const images = this.container.images
		if(!images || imageIndex >= images.length) {
			return Promise.reject(new Error("Failed to load image buffer"))
		}

		const image = images[imageIndex]
		if(image.uri) {
			const file = this.archive?.files.find(f => f.fileName == image.uri)
			if(file) {
				const data = await file.extract()
				return data
			}
		}

		if(image.bufferView !== undefined && this.container.bufferViews) {
			const bufferView = this.container.bufferViews[image.bufferView]
			if(bufferView) {
				const buffer = await this.loadBufferView(bufferView)
				return buffer
			}
		}

		return Promise.reject(new Error("Failed to load image buffer"))
	}

	private async loadNamedBuffer(type: GLTFBufferType): Promise<ArrayBufferLike> {
		if(!type.uri || !this.archive) {
			return Promise.reject(new Error("Failed to load buffer"))
		}

		const buff = this.buffers.get(type.uri)
		if(buff) {
			return buff
		}

		const file = this.archive?.files.find(f => f.fileName == type.uri)
		if(file) {
			const data = await file.extract()
			this.buffers.set(type.uri, data)
			return data
		}
		return Promise.reject(new Error("Failed to load buffer"))
	}

	public async loadIndexedBuffer(index: number): Promise<ArrayBufferLike> {
		if(!this.container.buffers || index >= this.container.buffers.length) {
			return Promise.reject(new Error("Failed to load buffer"))
		}
		const buffer = this.container.buffers[index]
		if(!buffer) {
			return Promise.reject(new Error("Failed to load buffer"))
		}

		// For GLB files, the buffer should not have a URI and should match the BIN chunk
		if(!buffer.uri && this.glbDataBuffer) {
			return this.glbDataBuffer
		}

		// For ZIP archives with separate buffer files
		if(buffer.uri && this.archive) {
			const file = this.archive.files.find(f => f.fileName == buffer.uri)
			if(file) {
				return await file.extract()
			}
		}

		// Fallback: try GLB buffer with length check
		if(buffer.byteLength && this.glbDataBuffer?.byteLength == buffer.byteLength) {
			return this.glbDataBuffer
		}

		return Promise.reject(new Error("Failed to load buffer"))
	}

	public async loadBufferView(type: GLTFBufferViewType): Promise<ArrayBufferLike> {
		const buffer = await this.loadIndexedBuffer(type.buffer)
		const byteOffset = type.byteOffset ?? 0
		const byteLength = type.byteLength ?? buffer.byteLength
		const dataWindow = buffer.slice(byteOffset, byteOffset + byteLength)
		return dataWindow
	}

	private async accessorToAttribute(accessor: GLTFAccessorType): Promise<VertexAttribute> {
		const container = this.container

		if(accessor.bufferView !== undefined && container.bufferViews && container.buffers) {
			const bufferView = container.bufferViews[accessor.bufferView]
			const buffer = container.buffers[bufferView.buffer]

			let dataWindow: ArrayBufferLike
			if (buffer.uri) {
				const data = await this.loadNamedBuffer(buffer)
				const byteOffset = bufferView.byteOffset ?? 0
				const byteLength = bufferView.byteLength ?? data.byteLength
				dataWindow = data.slice(byteOffset, byteOffset + byteLength)
			} else {
				dataWindow = await this.loadBufferView(bufferView)
			}
			if(!dataWindow) {
				logger.error(`[GLTF] Failed to load data window`)
				return Promise.reject(new Error("Failed to load attribute"))
			}

			const byteStride = bufferView.byteStride ?? 0
			const normalized = accessor.normalized === true
			const accessorByteOffset = accessor.byteOffset ?? 0

			// Determine the natural size of the attribute in bytes
			const componentsPerAttribute = accessor.type === "SCALAR" ? 1 : accessor.type === "VEC2" ? 2 : accessor.type === "VEC3" ? 3 : accessor.type === "VEC4" ? 4 : 0

			switch(accessor.componentType) {
				case ComponentType.Byte: {
					const int8Data = new Int8Array(dataWindow, accessorByteOffset)
					const naturalByteSize = componentsPerAttribute * 1
					const elementStride = (byteStride > 0 && byteStride > naturalByteSize) ? byteStride / 1 : 0
					return this.createAttribute(accessor.type, elementStride, int8Data, normalized, accessor.count)
				}
				case ComponentType.UnsignedByte: {
					const uint8Data = new Uint8Array(dataWindow, accessorByteOffset)
					const naturalByteSize = componentsPerAttribute * 1
					const elementStride = (byteStride > 0 && byteStride > naturalByteSize) ? byteStride / 1 : 0
					return this.createAttribute(accessor.type, elementStride, uint8Data, normalized, accessor.count)
				}
				case ComponentType.Short: {
					const int16Data = new Int16Array(dataWindow, accessorByteOffset)
					const naturalByteSize = componentsPerAttribute * 2
					const elementStride = (byteStride > 0 && byteStride > naturalByteSize) ? byteStride / 2 : 0
					return this.createAttribute(accessor.type, elementStride, int16Data, normalized, accessor.count)
				}
				case ComponentType.UnsignedShort: {
					const uint16Data = new Uint16Array(dataWindow, accessorByteOffset)
					const naturalByteSize = componentsPerAttribute * 2
					const elementStride = (byteStride > 0 && byteStride > naturalByteSize) ? byteStride / 2 : 0
					return this.createAttribute(accessor.type, elementStride, uint16Data, normalized, accessor.count)
				}
				case ComponentType.UnsignedInt: {
					const uint32Data = new Uint32Array(dataWindow, accessorByteOffset)
					const naturalByteSize = componentsPerAttribute * 4
					const elementStride = (byteStride > 0 && byteStride > naturalByteSize) ? byteStride / 4 : 0
					return this.createAttribute(accessor.type, elementStride, uint32Data, normalized, accessor.count)
				}
				case ComponentType.Float: {
					const floatData = new Float32Array(dataWindow, accessorByteOffset)
					const naturalByteSize = componentsPerAttribute * 4
					const elementStride = (byteStride > 0 && byteStride > naturalByteSize) ? byteStride / 4 : 0
					return this.createAttribute(accessor.type, elementStride, floatData, normalized, accessor.count)
				}
			}
		}
		logger.error(`[GLTF] Failed to load attribute - no valid bufferView`)
		return Promise.reject(new Error(`Failed to load attribute`))
	}

	private createAttribute(type: string, elementStride: number, data: Int8Array | Uint8Array | Int16Array | Uint16Array | Uint32Array | Float32Array, normalized: boolean, count: number): VertexAttribute {
		// Helper to normalize integer values to [0, 1] or [-1, 1] range
		const normalizeValue = (value: number, isSigned: boolean, maxValue: number): number => {
			if (!normalized) return value
			if (isSigned) {
				// Map to [-1, 1]
				return Math.max(value / maxValue, -1.0)
			} else {
				// Map to [0, 1]
				return value / maxValue
			}
		}

		// Determine max value for normalization based on data type
		let maxValue = 1
		let isSigned = false
		if (normalized) {
			if (data instanceof Int8Array) {
				maxValue = 127
				isSigned = true
			} else if (data instanceof Uint8Array) {
				maxValue = 255
				isSigned = false
			} else if (data instanceof Int16Array) {
				maxValue = 32767
				isSigned = true
			} else if (data instanceof Uint16Array) {
				maxValue = 65535
				isSigned = false
			}
		}

		const componentsPerAttribute = type === "SCALAR" ? 1 : type === "VEC2" ? 2 : type === "VEC3" ? 3 : type === "VEC4" ? 4 : 0
		if (componentsPerAttribute === 0) {
			this.context.logger.error(`Unknown attribute type: ${type}`)
			return new VertexAttribute(this.context, 0)
		}

		const attribute = new VertexAttribute(this.context, componentsPerAttribute)
		const totalComponents = count * componentsPerAttribute

		if(elementStride == 0) {
			// Tightly packed data - read exactly 'count' vertices worth of data
			const dataToRead = data.slice(0, totalComponents)
			if (normalized && (data instanceof Int8Array || data instanceof Uint8Array || data instanceof Int16Array || data instanceof Uint16Array)) {
				// Apply normalization
				const normalizedData = Array.from(dataToRead).map(v => normalizeValue(v, isSigned, maxValue))
				attribute.values = normalizedData
			} else {
				attribute.values = Array.from(dataToRead)
			}
		} else {
			// Strided data - elementStride is the number of elements between each attribute
			// Read exactly 'count' vertices
			for(let vertexIndex = 0; vertexIndex < count; vertexIndex++) {
				const baseIndex = vertexIndex * elementStride
				const values: number[] = []
				for(let j = 0; j < componentsPerAttribute; j++) {
					const rawValue = data[baseIndex + j]
					const value = normalized ? normalizeValue(rawValue, isSigned, maxValue) : rawValue
					values.push(value)
				}
				attribute.add(...values)
			}
		}

		return attribute
	}

	private async accessorToIndexBuffer(accessor: GLTFAccessorType): Promise<IndexBuffer> {
		const container = this.container

		if(accessor.bufferView !== undefined && container.bufferViews && container.buffers) {
			const bufferView: GLTFBufferViewType = container.bufferViews[accessor.bufferView]
			const buffer: GLTFBufferType = container.buffers[bufferView.buffer]

			let dataWindow: ArrayBufferLike
			if (buffer.uri) {
				const data = await this.loadNamedBuffer(buffer)
				const byteOffset = bufferView.byteOffset ?? 0
				const byteLength = bufferView.byteLength ?? data.byteLength
				dataWindow = data.slice(byteOffset, byteOffset + byteLength)
			} else {
				dataWindow = await this.loadBufferView(bufferView)
			}
			if(!dataWindow) {
				return Promise.reject(new Error("Failed to load index buffer"))
			}

			const accessorByteOffset = accessor.byteOffset ?? 0

			switch(accessor.componentType) {
				case ComponentType.Byte:
				case ComponentType.UnsignedByte:
				case ComponentType.Float:
					logger.error(`[GLTF] Unsupported index buffer component type: ${accessor.componentType}`)
					return Promise.reject(new Error("Unsupported data type for IndexBuffer"))

				case ComponentType.Short:
					return new IndexBuffer(this.context, new Int16Array(dataWindow, accessorByteOffset, accessor.count))

				case ComponentType.UnsignedShort:
					return new IndexBuffer(this.context, new Uint16Array(dataWindow, accessorByteOffset, accessor.count))

				case ComponentType.UnsignedInt:
					return new IndexBuffer(this.context, new Uint32Array(dataWindow, accessorByteOffset, accessor.count))
			}
		}
		return Promise.reject(new Error("Failed to load IndexBuffer"))
	}
}
